import { client, gql } from 'data-provider';
import { IAvatar, IMetaData, IProduct, ISort } from 'interfaces';
import { IAttribute } from './template';
import { IVariant } from './variant';

export interface IAllProductQuery {
  searchString?: string;
  start?: number;
  limit?: number;
}

interface IUpdateProductData {
  name: string;
  sku: string;
  productDetails: string;
  tags: string[];
  category: { id: string }[];
  avgPurchasePrice: number;
  avgSellingPrice: number;
  avatar: IAvatar[];
  productMetaData: IMetaData[];
}

interface IUpdateProductNewData {
  name: string;
  sku: string;
  description: string;
  productCategory: string;
  productTags: string[];
  purchasePrice: number;
  sellingPrice: number;
  photo?: IPhoto;
}

export interface IProductNew {
  id: string;
  name: string;
  sku: string;
  description: string;
  sellingPrice: number;
  purchasePrice: number;
  customFields: Omit<IAttribute, 'id'>[] | null;
  productType: {
    productTemplate: {
      id: string;
      name: string;
      productAttribute: IAttribute[];
    };
  }[];
  productTags: {
    tag: { id: string; name: string };
  }[];
  productCategory: { id: string; name: string } | null;
  photo: IPhoto;
}

export interface ExportProduct {
  id: string;
  name: string;
  sku: string;
  sellingPrice: number;
  purchasePrice: number;
  photo: { urL: string; thumbUrl: string } | null;
  tags: { id: string; name: string }[];
  category: { id: string; name: string } | null;
  description: string;
  customFields: Omit<IAttribute, 'id'>[] | null;
  variants: {
    id: string;
    name: string;
    sku: string;
    purchasePrice: number;
    sellingPrice: number;
    measurementUnit: { id: string; name: string } | null;
    make: boolean;
    sell: boolean;
    purchase: boolean;
    type: string;
    tags: { id: string; name: string }[];
    category: { id: string; name: string } | null;
    photo: { urL: string; thumbUrl: string } | null;
    description: string | null;
    customFields: { [key: string]: string }[] | null;
    variantAttributes: { [key: string]: string }[] | null;
  }[];
}

export type IPhoto = IAvatar;

interface CreateProductNewData {
  name: string;
  sku: string;
  description: string;
  purchasePrice: number;
  sellingPrice: number;
  productCategory: string | null;
  productTags: string[];
  productTemplates: string[];
  photo?: IPhoto;
  customFields: (Omit<IAttribute, 'id' | 'type'> & {
    type: 'Text' | 'Boolean' | 'Float' | 'Dropdown';
  })[];
}

interface CreateProductWithVariantData {
  name: string;
  sku: string;
  description: string;
  purchasePrice: number;
  sellingPrice: number;
  productCategory: string;
  productTags: string[];
  productWarehouses: string[];
  photo?: IPhoto;
  productTemplates: {
    templateIds: string[];
    variantAttributes: {
      attributeId: string;
      value: string;
    }[];
  };
  customFields: {
    name: string;
    type: 'Text' | 'Boolean' | 'Float' | 'Dropdown';
    value: string;
  }[];
}

export interface ProductWithVariantsData {
  name: string;
  productType: string;
  productCategory: string;
  productTags: string[];
  productWarehouses: string[];
  measurementUnit: string;
  make: boolean;
  purchase: boolean;
  sell: boolean;
  variantTemplateConfig: string[];
  variantAttributeConfig: {
    name: string;
    type: string;
    options: string[];
    required: boolean;
    allowCustomValue: boolean;
  }[];
  variantCustomFieldConfig: {
    name: string;
    type: string;
    options: string[];
    required: boolean;
    allowCustomValue: boolean;
  }[];
  variantsDetails: {
    id?: string;
    sku: string;
    sellingPrice: number;
    purchasePrice: number;
    photo: any;
    templateFields: {
      templateId: string;
      templateAttributeData: {
        [key: string]: boolean | string | number;
      };
    }[];
    customFields: {
      key: string;
      value: string;
    }[];
    attributeFields: {
      key: string;
      value: string;
    }[];
  }[];
}

export interface IVariantOfProduct {
  productId: string;
  productName: string;
  sku: string;
  productType: string;
  description: string | null;
  make: boolean;
  sell: boolean;
  purchase: boolean;
  measurementUnit: {
    id: string;
    name: string;
    abbreviation: string;
  };
  productWarehouses: string[];
  batchInfo?: {
    batchNo: string;
    expirationDate?: string | null;
    notes?: string | null;
  } | null;
  customFieldConfig: {
    name: string;
    type: 'text' | 'float' | 'boolean' | 'dropdown';
    options: string[];
    required: boolean;
    allowCustomValue: boolean;
  }[];
  variantConfig: {
    name: string;
    type: 'text' | 'float' | 'boolean' | 'dropdown';
    options: string[];
    required: boolean;
    allowCustomValue: boolean;
  }[];
  productVariants: (Omit<IVariant, 'productVariantPhoto'> & { photo: IPhoto })[];
}

type CreateProductCustomFieldData = {
  product_id: string;
} & Omit<IAttribute, 'id'>;

export interface IMaterialVariantDropdown {
  id: string;
  name: string;
  purchasePrice: number;
  sellingPrice: number;
}

export const getAllProduct = async (productQuery?: IAllProductQuery) => {
  const where = {
    query: productQuery && productQuery.searchString ? productQuery.searchString : '',
  };

  const query = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      products(where: $where, sort: $sort, start: $start, limit: $limit) {
        data {
          id
          name
          sku
          avgPurchasePrice
          avgSellingPrice
          productTags
          avatar {
            uid
            name
            url
            thumbUrl
            size
            type
            lastModifiedDate
          }
          productCategory {
            category {
              id
              name
            }
          }
          productMetaData {
            attribute
            value
          }
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: productQuery && productQuery.start ? productQuery.start : 1,
    limit: productQuery && productQuery.limit ? productQuery.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.products as {
    data: IProduct[];
    page: number;
    size: number;
    total: number;
  };
};

export const getAllProductDropdownData = async () => {
  const query = gql`
    query ProductDropdown {
      productDropdown {
        id
        name
        sku
        avgPurchasePrice
        avgSellingPrice
        productTags
        avatar {
          uid
          name
          url
          thumbUrl
          size
          type
          lastModifiedDate
        }
        productCategory {
          category {
            id
            name
          }
        }
        productMetaData {
          attribute
          value
        }
      }
    }
  `;

  const data = await client.request(query);
  return data.productDropdown as IProduct[];
};

export const getMaterialVariantsDropdown = async () => {
  const query = gql`
    query {
      materialVariantDropdown {
        id
        name
        purchasePrice
        sellingPrice
      }
    }
  `;

  const data = await client.request(query);
  return data.materialVariantDropdown as IMaterialVariantDropdown[];
};

export const getProductVariantRecipes = async (productId: string) => {
  const query = gql`
    query ($productId: String!) {
      productVariantRecipe(productId: $productId) {
        usedProductRecipes {
          usedVariantId
          usedVariantName
          usedProductQuantity
          usedProductUnitPrice
          materialCost
          note
        }
      }
    }
  `;

  const variables = { productId };
  const data = await client.request(query, variables);
  return (data.productVariantRecipe?.usedProductRecipes ?? []) as {
    usedVariantId: string;
    usedVariantName: string;
    usedProductQuantity: number;
    usedProductUnitPrice: number;
    materialCost: number;
    note: string | null;
  }[];
};

export const importProducts = async (data: any) => {
  const mutation = gql`
    mutation ($input: createInputCSVBulkProductInput) {
      importCSVProduct(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(mutation, variables);
  return createdData;
};

export const importProductsForCreate = async (data: any) => {
  const mutation = gql`
    mutation ($input: importCreateProductWithVariantInput!) {
      importCreateProductWithVariants(input: $input)
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(mutation, variables);
  return createdData;
};

export const importProductsForUpdate = async (data: any) => {
  const mutation = gql`
    mutation ($input: importUpdateProductWithVariantInput!) {
      importUpdateProductWithVariants(input: $input)
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(mutation, variables);
  return createdData;
};

export const getProduct = async (productId: string) => {
  const query = `
    query($productId: ID!) {
      product(id: $productId) {
        id
        name
        sku
        productDetails
        avgPurchasePrice
        avgSellingPrice
        productTags
        avatar {
          url
          thumbUrl
        }
        productCategory {
          category {
            name
          }
        }
        productMetaData {
          attribute
          value
        }
      }
    }
  `;

  const variables = { productId };
  const data = await client.request(query, variables);
  return data.product as IProduct;
};

export const updateProduct = async (id: string, data: IUpdateProductData) => {
  const query = gql`
    mutation ($input: updateProductInput) {
      updateProduct(input: $input) {
        id
        name
        sku
      }
    }
  `;

  const variables = { input: { where: { id }, data } };
  const updateProductData = await client.request(query, variables);
  return updateProductData.updateProduct as { id: string; name: string; sku: string };
};

export const getProductsNew = async (query?: {
  start?: number;
  limit?: number;
  searchString?: string;
  sort?: ISort | null;
}) => {
  const gqlQuery = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      productsCopy(where: $where, sort: $sort, start: $start, limit: $limit) {
        page
        size
        total
        data {
          id
          name
          sku
          description
          sellingPrice
          purchasePrice
          customFields
          productType {
            productTemplate {
              id
              name
            }
          }
          productTags {
            tag {
              id
              name
            }
          }
          productCategory {
            id
            name
          }
          photo {
            thumbUrl
            url
          }
        }
      }
    }
  `;

  const variables = {
    where: {
      query: query && query.searchString ? query.searchString : '',
    },
    sort: query?.sort,
    start: query && query.start ? query.start : 1,
    limit: query && query.limit ? query.limit : 10,
  };
  const data = await client.request(gqlQuery, variables);
  return data.productsCopy as {
    data: IProductNew[];
    page: number;
    size: number;
    total: number;
  };
};

export const getProductsDropdownNew = async () => {
  const query = gql`
    query {
      productCopyDropdown {
        id
        name
        sku
      }
    }
  `;

  const data = await client.request(query);
  return data.productCopyDropdown as { id: string; name: string; sku: string }[];
};

export const getProductNew = async (id: string) => {
  const query = gql`
    query ($productCopyId: ID!) {
      productCopy(id: $productCopyId) {
        id
        name
        sku
        description
        sellingPrice
        purchasePrice
        customFields
        productCategory {
          id
          name
        }
        productTags {
          tag {
            id
            name
          }
        }
        productType {
          productTemplate {
            id
            name
            productAttribute {
              id
              name
              type
              required
              allowCustomValue
              options
            }
          }
        }
        photo {
          url
          thumbUrl
        }
      }
    }
  `;

  const variables = { productCopyId: id };
  const data = await client.request(query, variables);
  return data.productCopy as IProductNew;
};

export const updateProductNew = async (id: string, data: IUpdateProductNewData) => {
  const query = gql`
    mutation ($input: UpdateProductCopyInput) {
      updateProductCopy(input: $input) {
        id
        name
        sku
      }
    }
  `;

  const variables = { input: { where: { id }, data } };
  const updateProductData = await client.request(query, variables);
  return updateProductData.updateProductCopy as { id: string; name: string; sku: string };
};

export const createProductNew = async (data: CreateProductNewData) => {
  const query = gql`
    mutation ($input: createProductCopyInput!) {
      createProductCopy(input: $input) {
        id
        name
        sku
      }
    }
  `;
  const variables = { input: { data } };
  const savedProduct = await client.request(query, variables);
  return savedProduct.createProductCopy as { id: string; name: string; sku: string };
};

export const deleteProductNew = async (id: string) => {
  const mutation = gql`
    mutation ($input: deleteProductCopyInput!) {
      deleteProductCopy(input: $input) {
        productCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
    },
  };
  const response = await client.request(mutation, variables);
  return response.deleteProductCopy as { productCopy: { id: string } };
};

export const createProductWithVariant = async (data: CreateProductWithVariantData) => {
  const query = gql`
    mutation ($input: createProductCopyWithVariantInput!) {
      createProductCopyWithVariant(input: $input) {
        id
        name
        sku
      }
    }
  `;
  const variables = { input: { data } };
  const savedProduct = await client.request(query, variables);
  return savedProduct.createProductCopyWithVariant as { id: string; name: string; sku: string };
};

export const createProductWithVariants = async (data: ProductWithVariantsData) => {
  const query = gql`
    mutation ($input: createProductWithVariantInput!) {
      createProductWithVariants(input: $input) {
        id
      }
    }
  `;
  const variables = { input: { data } };
  const savedProduct = await client.request(query, variables);
  return savedProduct.createProductWithVariants as { id: string };
};

export const updateProductWithVariants = async (
  productId: string,
  data: ProductWithVariantsData,
) => {
  const query = gql`
    mutation UpdateProductWithVariants($input: updateProductWithVariantInput!) {
      updateProductWithVariants(input: $input) {
        id
      }
    }
  `;
  const variables = {
    input: {
      where: {
        id: productId,
      },
      data,
    },
  };
  const savedProduct = await client.request(query, variables);
  return savedProduct.updateProductWithVariants as { id: string };
};

export const createProductRecipe = async (
  madeUpProductId: string,
  usedVariants: {
    usedVariantId: string;
    usedProductQuantity: number;
    materialCost: number;
    notes: string | null;
  }[],
) => {
  const query = gql`
    mutation ($input: createProductRecipeInput!) {
      createProductRecipe(input: $input) {
        id
      }
    }
  `;

  const variables = { input: { data: { madeUpProductId, usedVariants } } };
  const response = await client.request(query, variables);
  return response.createProductRecipe as { id: string };
};

export const getVariantsOfProduct = async (id: string) => {
  const query = gql`
    query ($productId: ID!) {
      variantsOfProduct(productId: $productId) {
        productId
        productName
        productType
        sku
        description
        make
        sell
        purchase
        measurementUnit {
          id
          name
          abbreviation
        }
        productWarehouses
        batchInfo
        customFieldConfig
        variantConfig
        productVariants {
          productVariantId
          productVariantName
          sku
          description
          purchasePrice
          sellingPrice
          customFields
          tags {
            id
            name
          }
          category {
            id
            name
          }
          photo {
            uid
            name
            url
          }
          productTemplates {
            templateId
            templateName
            templateType
            productTemplateAttributes {
              attributeId
              attributeName
              attributeType
              value
            }
          }
        }
      }
    }
  `;

  const variables = { productId: id };
  const data = await client.request(query, variables);
  return data.variantsOfProduct as IVariantOfProduct | null;
};

export const createProductCustomField = async (data: CreateProductCustomFieldData) => {
  const query = gql`
    mutation ($input: CreateCustomFieldInput!) {
      createProductCustomField(input: $input) {
        name
        type
        options
        required
        allowCustomValue
      }
    }
  `;

  const variables = { input: { data } };
  const savedData = await client.request(query, variables);
  return savedData.createProductCustomField as Omit<IAttribute, 'id' | 'type'> & {
    type: 'Text' | 'Boolean' | 'Float' | 'Dropdown';
  };
};

export const updateProductCustomField = async (
  productId: string,
  customFieldName: string,
  data: Omit<IAttribute, 'id'>,
) => {
  const query = gql`
    mutation ($input: UpdateCustomFieldInput!) {
      updateProductCustomField(input: $input) {
        name
        options
        required
        type
        allowCustomValue
      }
    }
  `;

  const variables = {
    input: {
      where: {
        id: productId,
        customFieldName,
      },
      data,
    },
  };
  const updatedData = await client.request(query, variables);
  return updatedData.updateProductCustomField as Omit<IAttribute, 'id' | 'type'> & {
    type: 'Text' | 'Boolean' | 'Float' | 'Dropdown';
  };
};

export const deleteProductCustomField = async (id: string, customFieldName: string) => {
  const mutation = gql`
    mutation ($input: DeleteCustomFieldInput!) {
      deleteProductCustomField(input: $input) {
        productCustomField {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: {
        id,
        customFieldName,
      },
    },
  };

  const response = await client.request(mutation, variables);
  return response.productCustomField as { id: string };
};

export const exportProducts = async (query?: string, sort?: ISort | null) => {
  const gqlQuery = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      exportCSVProductCopy(where: $where, sort: $sort, start: $start, limit: $limit) {
        data {
          id
          name
          sku
          purchasePrice
          sellingPrice
          photo {
            thumbUrl
            url
          }
          tags {
            id
            name
          }
          category {
            id
            name
          }
          description
          customFields
          variants {
            id
            name
            sku
            purchasePrice
            sellingPrice
            photo {
              thumbUrl
              url
            }
            tags {
              id
              name
            }
            category {
              id
              name
            }
            description
            customFields
          }
        }
      }
    }
  `;

  const variables = {
    where: { query: query ?? '' },
    sort,
  };
  const data = await client.request(gqlQuery, variables);
  return data.exportCSVProductCopy.data as ExportProduct[];
};

export const uploadProductMedia = async (files: Array<IAvatar & { base64String: String }>) => {
  const query = gql`
    mutation UploadProductMedia($input: productUploadMediaInput!) {
      uploadProductMedia(input: $input)
    }
  `;
  const variables = { input: { photo: files } };
  const savedData = await client.request(query, variables);
  return savedData.uploadProductMedia as string;
};

export const mapProductsAndImages = async () => {
  const query = gql`
    mutation Mutation {
      mergeProductMedia
    }
  `;

  const savedData = await client.request(query);
  return savedData.mergeProductMedia as string;
};

export const isProductVariantSkuUnique = async (code: string) => {
  const query = gql`
    query ($variantSku: String) {
      isVariantSkuUnique(variantSku: $variantSku)
    }
  `;

  const variables = {
    variantSku: code,
  };
  const data = await client.request(query, variables);
  return data.isVariantSkuUnique as boolean;
};
